import { useSelector } from 'react-redux'
import round from 'lodash/round'
import { evolve, pickAll, pipe } from 'ramda'
import { Formik, Form } from 'formik'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import * as Yup from 'yup'
import { select } from '@/store'
import { Button } from '@/components/button'
import { Row, FieldDropdown, Group } from '@/components/form'
import { createModal, useModalize } from '@/components/modalize/mui'
import useMediaQuery from '@/hooks/use-media-query'
import { Box } from '@/components/structure'
import { fontWeights, breakpoints } from '@/styles'
import ModalBase from '@/components/modal/modal-base'
import Context from '@/components/modal/context'
import useDirectDeposit from '@/hooks/use-direct-deposit'
import { defaultIfFalsy } from '@/utils'
import PadFields from './pad-fields'
import OneTimeFields from './one-time-fields'
import Alert from './alert'

const SubHeader = styled.p`
  font-size: 15px;
  font-weight: ${fontWeights.medium};
  padding-bottom: 20px;
  margin: 0px;
  text-align: left;
  width: 100%;
`

const FormWrapper = styled(Form)`
  width: 100%;
`

const pickPadValues = pickAll(['request_type', 'description', 'due_date', 'additional_fee'])
const pickOneTimeValues = pickAll(['request_type', 'description', 'due_date', 'requested_amount'])

const getPadValues = pipe(
  pickPadValues,
  evolve({ additional_fee: defaultIfFalsy(0) }),
  evolve({ additional_fee: x => round(x, 2) })
)

const getOneTimeValues = pipe(pickOneTimeValues, evolve({ requested_amount: x => round(x, 2) }))

const RequestPaymentModal = ({
  listingId,
  tenantId,
  contractId,
  showPad,
  isPadActive,
  isPadRequested,
  ModalTitle,
  ModalFooter
}) => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const dispatch = useDispatch()
  const contract = useSelector(state => select.contract.contractView(state, { contractId }))
  const displayMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)
  const createPayment = dispatch.payment.createPaymentRequest
  const getFirstPadDate = dispatch.settlements.getFirstPadDate
  const { data: accountData } = useDirectDeposit()
  const isPadEnabled = accountData?.financial_info !== undefined

  const submitPadRequest = async values => {
    const formattedValues = getPadValues(values)
    const response = await Promise.all([
      createPayment({
        listingId,
        tenantId,
        contractId,
        isPad: true,
        ...formattedValues
      }),
      getFirstPadDate({
        contractId,
        dueDate: values.due_date
      })
    ])
    if (response[0].ok) {
      close()
    }
  }

  const submitOneTimeRequest = async values => {
    const formattedValues = getOneTimeValues(values)
    const response = await createPayment({
      listingId,
      tenantId,
      contractId,
      isPad: false,
      ...formattedValues
    })
    if (response.ok) {
      close()
    }
  }

  const formOptions = () => ({
    initialValues: {
      request_type: '',
      deposit_account: '',
      description: '',
      requested_amount: '',
      due_date: '',
      rent_price: '',
      additional_fee: 0,
      payment_frequency: '',
      agree_to_terms: false
    },
    validationSchema: Yup.object().shape({
      request_type: Yup.string()
        .test(
          'is_pad_enabled',
          'You already have a pending PAD request for this listing',
          value => !isPadRequested || value !== 'pad'
        )
        .test(
          'is_pad_active',
          'Recurring PAD payments already active in this listing',
          value => !isPadActive || value !== 'pad'
        )
        .required('Please select a payment type.'),
      deposit_account: Yup.string().required(),
      description: Yup.string().when('request_type', {
        is: 'one_time',
        then: schema => schema.required()
      }),
      requested_amount: Yup.number().when('request_type', {
        is: type => type !== 'pad',
        then: schema => schema.min(1).max(25000).required()
      }),
      due_date: Yup.string().required(),
      rent_price: Yup.string(),
      additional_fee: Yup.number()
        .min(0, 'Amount cannot be lower than $0')
        .max(1000, 'Amount cannot exceed $1000'),
      payment_frequency: Yup.string(),
      agree_to_terms: Yup.bool().when('request_type', {
        is: 'pad',
        then: schema =>
          schema
            .oneOf([true])
            .required(
              'You must agree to the above terms before you can submit a PAD payment request.'
            )
      })
    }),
    onSubmit: async (values, { setSubmitting }) => {
      const isPadRequest = values.request_type === 'pad'
      isPadRequest ? await submitPadRequest(values) : await submitOneTimeRequest(values)
      setSubmitting(false)
    }
  })

  const getFields = value => {
    if (value === 'pad')
      return (
        <PadFields
          accountData={accountData}
          contractId={contractId}
          contractPrice={contract.price}
        />
      )
    else if (value === 'one_time') return <OneTimeFields accountData={accountData} />
    return null
  }

  const requestTypeOptions = [
    ...(showPad
      ? [
          {
            value: 'pad',
            label: t('l.payments.view.payment_type_pad.text', 'PAD - Recurring')
          }
        ]
      : []),
    {
      value: 'one_time',
      label: t('l.payments.view.payment_type_one_time.text', 'One-Time')
    }
  ]

  return (
    <Context.Provider
      value={{
        ModalTitle,
        ModalFooter,
        close
      }}
    >
      <ModalBase
        overflow="visible"
        title={t('l.payments.view.request_payment_modal.title', 'Payment Request')}
      >
        <SubHeader>{t('l.payments.view.request_payment_modal.subtitle', 'Payment Info')}</SubHeader>
        <Formik {...formOptions()}>
          {({ values, isSubmitting, submitForm, setValues, resetForm }) => (
            <FormWrapper>
              <Row>
                <Group>
                  <FieldDropdown
                    id="request_type"
                    name="request_type"
                    placeholder={t(
                      'l.payments.view.request_payment_modal_payment_type.label',
                      'Select payment type'
                    )}
                    required
                    autoFocus
                    options={requestTypeOptions}
                    onChange={e => {
                      e === 'pad'
                        ? setValues({
                            rent_price: contract.price,
                            payment_frequency: 'monthly',
                            description: 'Rent Payment'
                          })
                        : resetForm()
                    }}
                  />
                </Group>
              </Row>
              {getFields(values.request_type)}
              <Alert
                isPadActive={isPadActive}
                isPadRequested={isPadRequested}
                isPadEnabled={isPadEnabled}
              />
              <ModalFooter>
                <Box justify="right">
                  <Button
                    disabled={isSubmitting}
                    onClick={submitForm}
                    type="button"
                    theme="primary"
                    size={displayMobile ? 'medium' : 'small'}
                    width={displayMobile ? '100%' : 122}
                  >
                    {t('l.payments.view.request_payment_modal_send_request.button', 'Send Request')}
                  </Button>
                </Box>
              </ModalFooter>
            </FormWrapper>
          )}
        </Formik>
      </ModalBase>
    </Context.Provider>
  )
}

export const show = createModal(RequestPaymentModal, {
  width: '536px',
  sheet: true
})
